import { useMemo, useState } from 'react';
import { Icon } from '../../../components/icons';
import { Card, Field, Input, cn } from '../../../components/ui';
import { CopyButton, ToolPageLayout, type ToolPageContent } from './ToolPageLayout';

type Policy = '-all' | '~all' | '?all' | '+all';

interface KnownSender {
  id: string;
  label: string;
  include: string;
}

/**
 * Curated list of the most common ESPs that publish a known SPF include. Order
 * by "size of customer base" so the most likely match floats to the top of the
 * picker. Add more as we encounter them — the include strings come straight
 * from the providers' own docs.
 */
const KNOWN_SENDERS: KnownSender[] = [
  { id: 'google', label: 'Google Workspace', include: '_spf.google.com' },
  { id: 'm365', label: 'Microsoft 365', include: 'spf.protection.outlook.com' },
  { id: 'sendgrid', label: 'SendGrid', include: 'sendgrid.net' },
  { id: 'mailgun', label: 'Mailgun', include: 'mailgun.org' },
  { id: 'mailchimp', label: 'Mailchimp', include: 'servers.mcsv.net' },
  { id: 'amazonses', label: 'Amazon SES', include: 'amazonses.com' },
  { id: 'brevo', label: 'Brevo / Sendinblue', include: 'spf.brevo.com' },
  { id: 'postmark', label: 'Postmark', include: 'spf.mtasv.net' },
  { id: 'klaviyo', label: 'Klaviyo', include: '_spf.klaviyo.com' },
  { id: 'hubspot', label: 'HubSpot', include: '_spf.hubspotemail.net' },
  { id: 'zoho', label: 'Zoho Mail', include: 'zoho.com' },
  { id: 'fastmail', label: 'Fastmail', include: 'spf.messagingengine.com' },
];

interface SpfConfig {
  domain: string;
  includeOwnMx: boolean;
  includeOwnA: boolean;
  knownSenderIds: Set<string>;
  customIncludes: string;
  ip4Ranges: string;
  policy: Policy;
}

const INITIAL: SpfConfig = {
  domain: '',
  includeOwnMx: true,
  includeOwnA: false,
  knownSenderIds: new Set(),
  customIncludes: '',
  ip4Ranges: '',
  policy: '~all',
};

/**
 * Free SPF TXT-record generator. Pure client-side. Tracks DNS-lookup count so
 * we can warn before the user exceeds the SPF 10-lookup limit (each include /
 * a / mx mechanism counts; ip4/ip6 don't).
 */
export function SpfGeneratorPage() {
  const [cfg, setCfg] = useState<SpfConfig>(INITIAL);

  const { record, lookupCount } = useMemo(() => buildSpfRecord(cfg), [cfg]);

  const content: ToolPageContent = {
    slug: 'spf-generator',
    title: 'SPF record generator',
    tagline: 'Build a valid SPF record from the senders you authorise.',
    eyebrow: 'Free tool · No signup · 100% in-browser',
    description:
      'Free SPF record generator. Pick the senders you authorise (Google, Microsoft 365, SendGrid…), choose -all / ~all / ?all, copy the TXT record to your DNS.',
    icon: <Icon.ShieldCheck />,
    accent: 'allow',
    intro:
      'Tick every service that sends mail on your domain\'s behalf, set the policy, copy the record. The tool warns if you cross the SPF 10-DNS-lookup limit, which is the single most common reason an SPF record silently fails.',
    body: (
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1fr]">
        <Card className="p-6">
          <h2 className="text-sm font-semibold">Configuration</h2>
          <div className="mt-5 grid gap-4">
            <Field label="Your domain">
              <Input
                value={cfg.domain}
                onChange={(e) => setCfg({ ...cfg, domain: e.target.value.toLowerCase().trim() })}
                placeholder="example.com"
                maxLength={253}
                autoFocus
              />
            </Field>

            <div className="grid gap-2 text-[13.5px]">
              <label className="flex cursor-pointer items-center gap-2.5">
                <input
                  type="checkbox"
                  checked={cfg.includeOwnMx}
                  onChange={(e) => setCfg({ ...cfg, includeOwnMx: e.target.checked })}
                  className="h-4 w-4 accent-[var(--color-accent)]"
                />
                Allow your own MX servers (<code className="font-mono text-[12px] text-allow">mx</code>)
              </label>
              <label className="flex cursor-pointer items-center gap-2.5">
                <input
                  type="checkbox"
                  checked={cfg.includeOwnA}
                  onChange={(e) => setCfg({ ...cfg, includeOwnA: e.target.checked })}
                  className="h-4 w-4 accent-[var(--color-accent)]"
                />
                Allow your domain's A record (<code className="font-mono text-[12px] text-allow">a</code>)
              </label>
            </div>

            <Field label="Authorised senders" hint="Tick every ESP that sends mail on this domain's behalf.">
              <div className="grid gap-1.5 sm:grid-cols-2">
                {KNOWN_SENDERS.map((s) => {
                  const checked = cfg.knownSenderIds.has(s.id);
                  return (
                    <label key={s.id} className="flex cursor-pointer items-center gap-2 rounded-md border border-bordersoft bg-bg px-2.5 py-2 text-[12.5px] hover:border-accentdeep">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const next = new Set(cfg.knownSenderIds);
                          if (e.target.checked) next.add(s.id);
                          else next.delete(s.id);
                          setCfg({ ...cfg, knownSenderIds: next });
                        }}
                        className="h-4 w-4 accent-[var(--color-accent)]"
                      />
                      {s.label}
                    </label>
                  );
                })}
              </div>
            </Field>

            <Field label="Other include: domains" hint="Comma- or newline-separated. e.g. spf.acme.com, _spf.foo.com">
              <textarea
                value={cfg.customIncludes}
                onChange={(e) => setCfg({ ...cfg, customIncludes: e.target.value })}
                placeholder="spf.acme.com, _spf.bar.com"
                rows={2}
                className="block w-full rounded-md border border-border bg-bg px-3 py-2 font-mono text-[12px] text-txt placeholder:text-faint focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </Field>

            <Field label="IPv4 ranges" hint="Comma- or newline-separated. e.g. 192.0.2.10, 198.51.100.0/24">
              <textarea
                value={cfg.ip4Ranges}
                onChange={(e) => setCfg({ ...cfg, ip4Ranges: e.target.value })}
                placeholder="192.0.2.10, 198.51.100.0/24"
                rows={2}
                className="block w-full rounded-md border border-border bg-bg px-3 py-2 font-mono text-[12px] text-txt placeholder:text-faint focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </Field>

            <Field label="Enforcement policy" hint="~all is the recommended default; -all is strict; ?all is monitoring-only; +all should never be used.">
              <div className="inline-flex flex-wrap rounded-md border border-border bg-bg p-1 text-[12.5px]">
                {(['-all', '~all', '?all', '+all'] as Policy[]).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setCfg({ ...cfg, policy: p })}
                    className={cn(
                      'rounded px-3 py-1.5 font-mono transition',
                      cfg.policy === p ? 'bg-accent text-[#04141d]' : 'text-muted hover:bg-hover hover:text-txt',
                    )}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </Field>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-baseline justify-between">
            <h2 className="text-sm font-semibold">Your SPF record</h2>
            <CopyButton text={record} />
          </div>
          <div className="mt-3 font-mono text-[10.5px] uppercase tracking-wider text-faint">Hostname / Name</div>
          <pre className="mt-1 overflow-auto whitespace-pre-wrap break-all rounded-md border border-bordersoft bg-panel2 p-3 font-mono text-[12px] text-allow">{cfg.domain || 'yourdomain.com'}</pre>
          <div className="mt-4 font-mono text-[10.5px] uppercase tracking-wider text-faint">TXT value</div>
          <pre className="mt-1 overflow-auto rounded-md border border-bordersoft bg-panel2 p-3 font-mono text-[12px] text-allow whitespace-pre-wrap break-all">
            {record}
          </pre>

          <div
            className={cn(
              'mt-4 rounded-md border px-3 py-2 text-[12.5px]',
              lookupCount > 10
                ? 'border-review/30 bg-review/10 text-review'
                : lookupCount > 7
                  ? 'border-verify/30 bg-verify/10 text-verify'
                  : 'border-allow/30 bg-allow/10 text-allow',
            )}
          >
            DNS lookups: <span className="font-mono">{lookupCount}</span> / 10 max.{' '}
            {lookupCount > 10
              ? 'Over the SPF limit — many receivers will return PermError and ignore the record. Drop some includes or flatten them.'
              : lookupCount > 7
                ? 'Getting close to the SPF 10-lookup hard limit.'
                : 'Well within the SPF 10-lookup limit.'}
          </div>

          <p className="mt-4 text-[12.5px] text-muted">
            Publish as a TXT record on the root of your domain (not a subdomain). Allow ~24h for propagation, then{' '}
            <a href="/tools/spf-checker/" className="text-accent hover:underline">verify with our SPF checker</a>.
          </p>
        </Card>
      </div>
    ),
    educational: {
      title: 'What this generator watches for',
      paragraphs: [
        "The tool counts DNS lookups as you add senders, because the 10-lookup ceiling is the failure that voids a record silently — every include:, a, mx and exists: counts, including the ones nested inside your providers' records.",
        'It defaults to ~all. Stay there until your DMARC reports confirm every legitimate sender is covered, then consider -all.',
      ],
    },
    faq: [
      { q: 'Where do I publish the record?', a: 'As a TXT record on the root of your domain (the same name where you set your DMARC at _dmarc.yourdomain.com lives one level deeper).' },
      { q: 'Can I have more than one SPF record?', a: 'No. Multiple v=spf1 records is a misconfiguration — receivers treat it as PermError. Merge everything into one record.' },
      { q: 'What\'s the 10-lookup limit?', a: 'SPF caps DNS lookups at 10 during evaluation. Each include:, a, mx, exists: mechanism counts. Crossing it returns PermError — the same as having no SPF.' },
      { q: 'Should I use -all or ~all?', a: '~all (soft fail) for most senders. -all (hard fail) only after DMARC reports confirm every legitimate sender is covered, otherwise legitimate mail will bounce.' },
      { q: 'I use 5 ESPs and keep hitting 10 lookups — what do I do?', a: 'Use SPF flattening: a service rewrites all your include: mechanisms into a single ip4: list. SieveGuard\'s Domain Health add-on flags lookup-limit overflows automatically as part of the daily health check.' },
    ],
    relatedTools: [
      { slug: 'dmarc-generator', title: 'DMARC record generator', tagline: 'Build a valid DMARC TXT record with the right policy + reports.' },
      { slug: 'dkim-generator', title: 'DKIM key generator', tagline: 'Generate an RSA keypair + TXT record in your browser.' },
      { slug: 'spf-checker', title: 'SPF record checker', tagline: 'Verify your published SPF record looks right.' },
    ],
    ctaTitle: 'Catch SPF drift before deliverability tanks',
    ctaBody:
      "SieveGuard's Domain Health add-on tracks SPF, DKIM, DMARC + MX on every domain you ingest. Drift alerts when a record changes, lookup-limit warnings when you add too many ESPs. Free during your trial.",
  };

  return <ToolPageLayout content={content} />;
}

function buildSpfRecord(cfg: SpfConfig): { record: string; lookupCount: number } {
  const parts: string[] = ['v=spf1'];
  let lookups = 0;

  if (cfg.includeOwnA) {
    parts.push('a');
    lookups += 1;
  }
  if (cfg.includeOwnMx) {
    parts.push('mx');
    lookups += 1;
  }

  for (const sender of KNOWN_SENDERS) {
    if (cfg.knownSenderIds.has(sender.id)) {
      parts.push(`include:${sender.include}`);
      lookups += 1;
    }
  }

  splitList(cfg.customIncludes).forEach((host) => {
    parts.push(`include:${host}`);
    lookups += 1;
  });

  splitList(cfg.ip4Ranges).forEach((range) => {
    parts.push(`ip4:${range}`);
  });

  parts.push(cfg.policy);
  return { record: parts.join(' '), lookupCount: lookups };
}

function splitList(raw: string): string[] {
  return raw
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

// Default export required by React Router framework-mode route modules.
export default SpfGeneratorPage;
